import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import gettext from 'js-utils/gettext';
import classNames from 'classnames';
import find from 'lodash/find';

import {
    SIZE_BLOCK,
    STYLE_NEUTRAL,
} from 'eventbrite_design_system/button/constants';
import Tag from 'eventbrite_design_system/tag/Tag';
import Button from 'eventbrite_design_system/button/Button';
import Icon from 'eventbrite_design_system/icon/Icon';
import FilterChunkySvg from 'eventbrite_design_system/iconography/icons/FilterChunky';
import DateFilter from './DateFilter';

import {
    CATEGORY_FILTER,
    DATE_FILTER,
    FORMAT_FILTER,
    PRICE_FILTER,
    TAG_BACKGROUND_COLOR,
    FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP,
    CLEAR_PRICE_FILTER,
} from '../constants';

import './facetedSearchFilters.scss';

const getRenderedFilters = (
    date,
    format,
    price,
    category,
    onClose,
    onActiveToggle,
) => {
    const filterObject = {
        [DATE_FILTER]: date || '',
        [CATEGORY_FILTER]: category || '',
        [FORMAT_FILTER]: format || '',
        [PRICE_FILTER]: price || CLEAR_PRICE_FILTER,
    };

    // we always want to show date even when it's not selected
    const filtersToShow = [
        DATE_FILTER,
        CATEGORY_FILTER,
        FORMAT_FILTER,
        PRICE_FILTER,
    ];

    return filtersToShow.map((key) => {
        // Given an applied filters value, grab the appropriate
        // display value associated to that filter
        const dropdownVals = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].values;
        const label = FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP[key].label;

        const foundFilter = find(
            dropdownVals,
            ({ value }) => value === filterObject[key],
        );

        let display;

        if (foundFilter) {
            display = foundFilter.display;
        } else {
            // This is for handling pick a date, since it is variable
            // it doesn't show up in the FACETED_SEARCH_DROPDOWN_FILTER_PROP_MAP array
            display = date;
        }

        let isClosable = true;

        if (!filterObject[key]) {
            isClosable = false;
        }

        if (key === PRICE_FILTER && !price) {
            isClosable = false;
        }

        return (
            <span className="facet eds-l-pad-left-3" key={key}>
                <Tag
                    initialIsActive={false}
                    key={key}
                    isClosable={isClosable}
                    isStatic={isClosable}
                    backgroundColor={
                        isClosable ? TAG_BACKGROUND_COLOR : undefined
                    }
                    onClose={onClose.bind(null, key)}
                    onActiveToggle={() => onActiveToggle(key)}
                >
                    {isClosable ? display : label}
                </Tag>
            </span>
        );
    });
};

const MoreFiltersToggle = ({ onClick, spacingClass }) => {
    const btnProps = {
        onClick,
        style: STYLE_NEUTRAL,
        size: SIZE_BLOCK,
    };

    return (
        <div className={`${spacingClass} more-filters`}>
            <Button {...btnProps}>
                <Icon type={<FilterChunkySvg />} />
                <span className="eds-text-bs">{gettext('Filters')}</span>
            </Button>
        </div>
    );
};

class FacetedSearchFiltersNewSearch extends PureComponent {
    static propTypes = {
        trackGAEvent: PropTypes.func,
        handleApplyFilter: PropTypes.func,
        handleRemoveFilter: PropTypes.func,
        selectedCategory: PropTypes.string,
        selectedPrice: PropTypes.string,
        selectedDate: PropTypes.string,
        selectedFormat: PropTypes.string,
        isBotRequest: PropTypes.bool,
        locationSlug: PropTypes.string,
        facets: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                count: PropTypes.number,
                displayName: PropTypes.string,
            }),
        ),
        isLoading: PropTypes.bool,

        showFocusDrawer: PropTypes.func,
        closeFocusDrawer: PropTypes.func,
        addFocusDrawerNotification: PropTypes.func,
        hideFocusDrawerNotification: PropTypes.func,
    };

    constructor(props) {
        super(props);

        const {
            selectedCategory,
            selectedDate,
            selectedFormat,
            selectedPrice,
        } = props;

        this.state = {
            [DATE_FILTER]: selectedDate,
            [CATEGORY_FILTER]: selectedCategory,
            [FORMAT_FILTER]: selectedFormat,
            [PRICE_FILTER]: selectedPrice,
            [`initial${CATEGORY_FILTER}`]: selectedCategory,
            [`initial${FORMAT_FILTER}`]: selectedFormat,
            [`initial${PRICE_FILTER}`]: selectedPrice,
        };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps({
        selectedCategory: newCategory,
        selectedPrice: newPrice,
        selectedDate: newDate,
        selectedFormat: newFormat,
    }) {
        this.setState(
            ({
                [DATE_FILTER]: currentDate,
                [PRICE_FILTER]: currentPrice,
                [CATEGORY_FILTER]: currentCategory,
                [FORMAT_FILTER]: currentFormat,
            }) => {
                let price = currentPrice;

                let category = currentCategory;

                let date = currentDate;

                let format = currentFormat;

                if (currentDate !== newDate) {
                    date = newDate;
                }

                if (currentCategory !== newCategory) {
                    category = newCategory;
                }

                if (currentPrice !== newPrice) {
                    price = newPrice;
                }

                if (currentFormat !== newFormat) {
                    format = newFormat;
                }

                return {
                    [DATE_FILTER]: date,
                    [PRICE_FILTER]: price,
                    [CATEGORY_FILTER]: category,
                    [FORMAT_FILTER]: format,
                    [`initial${CATEGORY_FILTER}`]: category,
                    [`initial${FORMAT_FILTER}`]: format,
                    [`initial${PRICE_FILTER}`]: price,
                    hasMoreFilters: price || category || format,
                };
            },
        );
    }

    _handleRemoveFilter(filterType) {
        const {handleRemoveFilter} = this.props;

        this.setState({[filterType]: ''}, () => {
            handleRemoveFilter({[filterType]: true});
        });
    }

    render() {
        const {
            isBotRequest,
            selectedDate,
            selectedCategory,
            selectedPrice,
            selectedFormat,
            openCloseFilterPanelMobile,
            locationSlug,
            isLoading,
        } = this.props;

        const {
            shouldShowPicker,
            [DATE_FILTER]: date,
        } = this.state;

        const classes = classNames(
            'faceted-search-filters',
            'faceted-search-filters--new-search',
            'eds-l-pad-hor-4',
            // always show them when it's a bot
            { 'eds-show': isBotRequest },
        );
        const scrollablePaneClasses = classNames(
            'faceted-search-filters__facets',
            // Give scrollbar some space
            'eds-l-pad-bot-3',
        );

        const handleOpenFilterPanel = (filterType) => openCloseFilterPanelMobile(true, filterType);

        const appliedFiltersAsFacets = getRenderedFilters(
            selectedDate,
            selectedFormat,
            selectedPrice,
            selectedCategory,
            this._handleRemoveFilter.bind(this),
            handleOpenFilterPanel,
        );

        const allFacets = [...appliedFiltersAsFacets];

        return (
            <section className={classes} data-spec="faceted-search-filters">
                <div className={scrollablePaneClasses} ref={this._setRef}>
                    <MoreFiltersToggle
                        onClick={() => openCloseFilterPanelMobile(true)}
                        spacingClass="eds-show-down-sw"
                    />
                    {allFacets}
                </div>
            </section>
        );
    }
}

export default FacetedSearchFiltersNewSearch;
